import { Box, IconButton, Menu, MenuItem, Stack } from '@mui/material';

import React, { useState, useRef, useEffect } from 'react';

import ReactDOMServer from 'react-dom/server';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import { ReactComponent as Bold } from '../../../../../../common/assets/images/bold.svg';
import { ReactComponent as Centeralign } from '../../../../../../common/assets/images/center-text.svg';
import { ReactComponent as Italic } from '../../../../../../common/assets/images/italic.svg';
import { ReactComponent as Justify } from '../../../../../../common/assets/images/justify.svg';
import { ReactComponent as Leftalign } from '../../../../../../common/assets/images/left-text.svg';
import { ReactComponent as Rightalign } from '../../../../../../common/assets/images/right-text.svg';
import { ReactComponent as FontFamily } from '../../../../../../common/assets/images/text-edit-fontfamily.svg';
import { ReactComponent as FontSize } from '../../../../../../common/assets/images/text-edit-fontsize.svg';

import styles from './TextEdit.module.scss';

const { iconFillColor } = styles;

interface IImpactSummaryProps {
	updatedSummary: string;
	setUpdatedSummary: React.Dispatch<React.SetStateAction<string>>;
	clicked: boolean;
	setClicked: React.Dispatch<React.SetStateAction<boolean>>;
	impactData: any;
}
const TextEdit: React.FunctionComponent<IImpactSummaryProps> = (props) => {
	const { updatedSummary, setUpdatedSummary, clicked, setClicked, impactData } =
		props;
	const [editorHtml, setEditorHtml] = useState('');
	const [anchorFamilyEl, setAnchorFamilyEl] = useState<null | HTMLElement>(
		null,
	);
	const [anchorSizeEl, setAnchorSizeEl] = useState<null | HTMLElement>(null);
	const fontSize = ReactQuill.Quill.import('attributors/style/size');
	fontSize.whitelist = ['11px', '20px', '30px'];
	ReactQuill.Quill.register(fontSize, true);
	// to set the default value in the editor
	useEffect(() => {
		if (impactData) {
			setEditorHtml(impactData.comments);
		}
	}, [impactData]);
	const quillRef = useRef<ReactQuill>(null);
	const icons = ReactQuill.Quill.import('ui/icons');
	icons.bold = ReactDOMServer.renderToString(
		<Bold fill={iconFillColor} className={styles['icon-padding']} />,
	);
	icons.italic = ReactDOMServer.renderToString(
		<Italic fill={iconFillColor} className={styles['icon-padding']} />,
	);
	icons.align[''] = ReactDOMServer.renderToString(
		<Leftalign fill={iconFillColor} className={styles['icon-padding']} />,
	);
	icons.align.center = ReactDOMServer.renderToString(
		<Centeralign fill={iconFillColor} className={styles['icon-padding']} />,
	);
	icons.align.right = ReactDOMServer.renderToString(
		<Rightalign fill={iconFillColor} className={styles['icon-padding']} />,
	);
	icons.align.justify = ReactDOMServer.renderToString(
		<Justify fill={iconFillColor} className={styles['icon-padding']} />,
	);
	const modules = {
		toolbar: {
			container: [
				['bold', 'italic'],
				[
					{ align: '' },
					{ align: 'center' },
					{ align: 'right' },
					{ align: 'justify' },
				],
			],
			style: true,
		},
	};
	const handleFamilyClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorFamilyEl(event.currentTarget);
	};
	const handleSizeClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorSizeEl(event.currentTarget);
	};
	const handleMenuClose = () => {
		setAnchorFamilyEl(null);
		setAnchorSizeEl(null);
	};
	const handleFontFamilyClick = (font: string) => {
		if (quillRef.current) {
			const quillInstance = quillRef.current.getEditor();
			const { index: range, length } = quillInstance.getSelection() ?? {};
			if (range !== undefined && length !== undefined) {
				quillInstance.formatText(range, length, 'font', false);
				quillInstance.formatText(range, length, 'font', font);
			}
		}
		handleMenuClose();
	};
	const handleFontSizeClick = (fontSize: string) => {
		if (quillRef.current) {
			const quillInstance = quillRef.current.getEditor();
			const { index: range, length } = quillInstance.getSelection() ?? {};
			if (range !== undefined && length !== undefined) {
				quillInstance.formatText(range, length, 'font-size', fontSize);
			}
		}
		handleMenuClose();
	};
	const handleEditorChange = (e: any) => {
		const value = e === '<p><br></p>' ? '' : e;
		setUpdatedSummary(value);
	};
	// to clear the editor after save or cancel are clicked
	useEffect(() => {
		if (clicked) {
			setEditorHtml('');
			setClicked(false);
		}
	}, [clicked, editorHtml]);

	return (
		<div
			className={styles['impact-edit-toolbar-box']}
			data-testid="impact-textedit-quill"
		>
			<Stack direction="row" alignItems="flex-start">
				<Stack
					direction="row"
					spacing={1.5}
					className={styles['impact-edit-font']}
				>
					<Box
						className={styles['impact-edit-font-family']}
						onClick={handleFamilyClick}
						data-testid="impact-textedit-family"
					>
						<IconButton sx={{ padding: 0 }}>
							<FontFamily fill={iconFillColor} />
						</IconButton>
					</Box>
					<Menu
						anchorEl={anchorFamilyEl}
						open={Boolean(anchorFamilyEl)}
						onClose={handleMenuClose}
						data-testid="family-menu"
					>
						<MenuItem
							className={styles['impact-family-monospace']}
							onClick={() => handleFontFamilyClick('monospace')}
							data-testid="impact-family-monospace"
						>
							monospace
						</MenuItem>
						<MenuItem
							className={styles['impact-family-sans-serif']}
							onClick={() => handleFontFamilyClick('sans-serif')}
							data-testid="impact-family-serif"
						>
							sans-serif
						</MenuItem>
						<MenuItem
							className={styles['impact-family-serif']}
							onClick={() => handleFontFamilyClick('serif')}
							data-testid="impact-family-san-serif"
						>
							serif
						</MenuItem>
					</Menu>
					<Box
						className={styles['impact-edit-font-size']}
						onClick={handleSizeClick}
						data-testid="impact-textedit-size"
					>
						<IconButton sx={{ padding: 0 }}>
							<FontSize fill={iconFillColor} />
						</IconButton>
					</Box>
					<Menu
						anchorEl={anchorSizeEl}
						open={Boolean(anchorSizeEl)}
						onClose={handleMenuClose}
						data-testid="size-menu"
					>
						<MenuItem
							className={styles['impact-size-11']}
							onClick={() => handleFontSizeClick('11px')}
							data-testid="impact-size-11"
						>
							11px
						</MenuItem>
						<MenuItem
							className={styles['impact-size-20']}
							onClick={() => handleFontSizeClick('20px')}
							data-testid="impact-size-20"
						>
							20px
						</MenuItem>
						<MenuItem
							className={styles['impact-size-30']}
							onClick={() => handleFontSizeClick('30px')}
							data-testid="impact-size-30"
						>
							30px
						</MenuItem>
					</Menu>
				</Stack>
				<ReactQuill
					ref={quillRef}
					value={updatedSummary}
					onChange={handleEditorChange}
					modules={modules}
					className={styles['impact-edit-toolbar']}
				/>
			</Stack>
		</div>
	);
};
export default TextEdit;
